import { Option, Select, Theme } from "@deere/fuel-react";
import LaunchIcon from "@mui/icons-material/Launch";
import { Button, Typography } from "@mui/material";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridCellCheckboxRenderer,
  GridColDef,
  GridColumnHeaderParams,
  GridHeaderCheckbox,
  GridRenderCellParams,
  GridRenderHeaderFilterProps,
  GridRowId,
  useGridApiContext,
  useGridApiEventHandler,
} from "@mui/x-data-grid-pro";
import { Dayjs } from "dayjs";
import { TFunction } from "i18next";
import React from "react";
import { Link } from "react-router-dom";
import InvoicesGridActionMenu from "./InvoicesGridActionMenu";
import {
  FilterType,
  InvoicesGridActionMenuOptions,
  InvoicesGridFilterProps,
  InvoicesRows,
} from "./types";
import ControlledHeaderFilter from "../../../../components/DataGridPro/HeaderFilters/controlledHeaderFilter/ControlledHeaderFilter";
import InvoicesDateRangePicker from "../invoicesDateRangePicker/InvoicesDateRangePicker";

const orderTypeTranslationKeys: Record<string, string> = {
  MD: "EmergencyOrder",
  SO: "StockOrder",
  PR: "PromotionOrder",
};

export function columnDef(
  theme: Theme,
  gridTexts: GridTexts,
  t: TFunction,
  filterProps: InvoicesGridFilterProps,
  dealerAccountId: string,
  showHULink: boolean,
  hasAccessToViewClaim: boolean,
  handleNFEClick: (invoiceNumber: string, nfeNumber: string) => void,
  invoicesGridActionMenu: InvoicesGridActionMenuOptions,
  selectionChanges: {
    onHeaderSelectChange: (value: boolean) => void;
    onRowSelectChange: (value: boolean, rowId: GridRowId) => void;
  },
  isNFePDFDownloadLoading?: string,
  isDealerIdPersistenceEnabledForHU?: boolean,
  data: InvoicesRows[] = []
): GridColDef[] {
  const {
    filters,
    disableFilters,
    setFilterValue,
    clearFilterValue,
    onDateRangeChange,
  } = filterProps;

  const orderTypeOptions = Array.from(
    new Set(
      data
        .map((row) => row.orderType)
        .filter((orderType): orderType is string => Boolean(orderType))
    )
  );

  const translateInvoiceType = (invoiceType: string) => {
    if (!invoiceType) {
      return null;
    }

    const translationKey = invoiceType.replace(/[ \-/]/g, "");

    const translation = t(`invoices:allInvoices.invoiceType.${translationKey}`);

    if (translation === `allInvoices.invoiceType.${translationKey}`) {
      return invoiceType;
    }

    return translation;
  };

  const translateOrderType = (orderType: string) => {
    if (!orderType) {
      return null;
    }

    const translationKey =
      orderTypeTranslationKeys[orderType] ?? orderType.replace(/[ \-/]/g, "");
    const translation = t(`invoices:allInvoices.orderType.${translationKey}`);

    if (translation === `allInvoices.orderType.${translationKey}`) {
      return orderType;
    }

    return translation;
  };

  const renderCellStyle = {
    lineHeight: theme.tokens.FuelLineHeightMd,
    fontSize: theme.tokens.FuelFontSizeSm,
  };

  return [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      sortable: false,
      disableColumnMenu: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <div
          style={{
            marginLeft: theme.tokens.FuelSpacing4,
          }}
        >
          <CustomGridHeaderCheckbox
            params={params}
            onHeaderSelectChange={selectionChanges.onHeaderSelectChange}
          />
        </div>
      ),
      renderCell: (params: GridRenderCellParams) =>
        params.row.invoiceNumber ? (
          <CustomGridCheckboxRenderer
            params={params}
            onRowSelectChange={selectionChanges.onRowSelectChange}
          />
        ) : (
          <></>
        ),
      align: "left",
      minWidth: 78,
    },
    {
      field: "dealerAccount",
      headerName: gridTexts.dealerAccount,
      renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
        <ControlledHeaderFilter
          dataTestId="dealer-account-search"
          inputRef={inputRef}
          disabled={disableFilters}
          value={filters.dealerId ?? ""}
          onChange={(value) => setFilterValue(value, FilterType.DealerAccount)}
          onClear={() => clearFilterValue(FilterType.DealerAccount)}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>
          {translateOrderType(params.value)}
        </Typography>
      ),
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "invoiceNumber",
      headerName: gridTexts.invoiceNumber,
      renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
        <ControlledHeaderFilter
          dataTestId="invoice-number-search"
          inputRef={inputRef}
          disabled={disableFilters}
          value={filters.invoiceNumber ?? ""}
          onChange={(value) => setFilterValue(value, FilterType.InvoiceNumber)}
          onClear={() => clearFilterValue(FilterType.InvoiceNumber)}
        />
      ),
      renderCell: (params: GridRenderCellParams<any, string>) => {
        const dealerAccountOwner = params.row.dealerAccount;
        const redirectUrl = `/invoices/${params.value}`;
        return (
          <Link
            key={params.id}
            to={redirectUrl}
            state={{
              ...(dealerAccountOwner && {
                dealerAccountId: dealerAccountOwner,
              }),
            }}
            data-testid={`${params.value}-invoice-details-link`}
            style={{
              color: theme.tokens.FuelColorTextBrandPrimary,
            }}
          >
            {params.value}
          </Link>
        );
      },
      disableColumnMenu: true,
      sortable: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "factura",
      headerName: gridTexts.factura,
      renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
        <ControlledHeaderFilter
          dataTestId="factura-search"
          inputRef={inputRef}
          disabled={disableFilters}
          value={filters.factura ?? ""}
          onChange={(value) => setFilterValue(value, FilterType.Factura)}
          onClear={() => clearFilterValue(FilterType.Factura)}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={renderCellStyle}
          data-testid={`${params.value}-factura-number`}
        >
          {params.value}
        </Typography>
      ),
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "invoiceType",
      headerName: gridTexts.invoiceType,
      renderHeaderFilter: () => (
        <Select
          data-testid="invoice-type-select"
          disabled={disableFilters}
          value={filters.invoiceType}
          onChange={(e) => {
            if (typeof e.target.value === "string") {
              setFilterValue(e.target.value, FilterType.InvoiceType);
            }
          }}
        >
          {filters.invoiceTypeList.map((invoiceType) => (
            <Option key={invoiceType.key} value={invoiceType.value}>
              {t(`invoices:allInvoices.invoiceType.${invoiceType.key}`)}
            </Option>
          ))}
        </Select>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>
          {" "}
          {translateInvoiceType(params.row.invoiceType)}
        </Typography>
      ),
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "orderType",
      headerName: gridTexts.orderType,
      renderHeaderFilter: () => (
        <Select
          hasSearch
          hasSelectAll
          multiple
          data-testid="order-type-select"
          disabled={disableFilters}
          value={filters.srcProcessType?.split(",").filter(Boolean) ?? []}
          onChange={(event) => {
            const selectedValues = Array.from(
              event.target.selectedOptions,
              (option) => option.value
            );
            setFilterValue(
              selectedValues.join(","),
              FilterType.SourceProcessType
            );
          }}
        >
          {orderTypeOptions.map((orderType) => (
            <Option key={orderType} value={orderType}>
              {translateOrderType(orderType)}
            </Option>
          ))}
        </Select>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>
          {translateOrderType(params.value)}
        </Typography>
      ),
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "nfeNumber",
      headerName: gridTexts.nfeNumber,
      renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
        <ControlledHeaderFilter
          dataTestId="nfe-search"
          inputRef={inputRef}
          disabled={disableFilters}
          value={filters.nfeNumber ?? ""}
          onChange={(value) =>
            setFilterValue(value, FilterType.NotaFiscalNumber)
          }
          onClear={() => clearFilterValue(FilterType.NotaFiscalNumber)}
        />
      ),
      renderCell: (params: GridRenderCellParams) =>
        params.row.showNfeLink ? (
          <Button
            disabled={isNFePDFDownloadLoading === params.value}
            onClick={() =>
              handleNFEClick(params.row.invoiceNumber, params.value)
            }
            variant="text-inline"
            data-testid={`${params.value}-nfe-attachment-link`}
          >
            {params.value}
            {params.value && (
              <LaunchIcon
                fontSize="small"
                sx={{
                  position: "relative",
                  left: "4px",
                }}
              />
            )}
          </Button>
        ) : (
          <Typography
            sx={renderCellStyle}
            data-testid={`${params.value}-nfe-number`}
          >
            {params.value}
          </Typography>
        ),
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "officialDocumentNumber",
      headerName: gridTexts.officialDocumentNumber,
      renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
        <ControlledHeaderFilter
          dataTestId="official-document-number-search"
          inputRef={inputRef}
          disabled={disableFilters}
          value={filters.officialDocumentNumber ?? ""}
          onChange={(value) =>
            setFilterValue(value, FilterType.OfficialDocumentNumber)
          }
          onClear={() => clearFilterValue(FilterType.OfficialDocumentNumber)}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "documentNumber",
      headerName: gridTexts.documentNumber,
      renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
        <ControlledHeaderFilter
          dataTestId="document-number-search"
          inputRef={inputRef}
          disabled={disableFilters}
          value={filters.documentNumber || ""}
          onChange={(value) => setFilterValue(value, FilterType.DocumentNumber)}
          onClear={() => clearFilterValue(FilterType.DocumentNumber)}
        />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const accountId = params.row.dealerAccount;
        const isCreditOrDebitMemo =
          params.row.invoiceType === "Credit Memo" ||
          params.row.invoiceType === "Debit Memo";
        const viewClaimsAccess = hasAccessToViewClaim && isCreditOrDebitMemo;

        return viewClaimsAccess
          ? [
              <Link
                key={params.id}
                to={`/claims/${params.value}`}
                state={{
                  ...(accountId && {
                    dealerAccountId: accountId,
                  }),
                }}
                data-testid={`${params.value}-claim-details-link`}
                style={{
                  color: theme.tokens.FuelColorTextBrandPrimary,
                }}
              >
                {params.value}
              </Link>,
            ]
          : [
              <Typography
                data-testid={`${params.value}-claim-details`}
                sx={renderCellStyle}
                key={params.id}
              >
                {params.value}
              </Typography>,
            ];
      },
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: "handlingUnit",
      headerName: gridTexts.handlingUnit,
      renderCell: showHULink
        ? (params: GridRenderCellParams) => (
            <Link
              key={params.id}
              to={
                isDealerIdPersistenceEnabledForHU
                  ? `/handlingUnits/${params.value}`
                  : `/dealer/${dealerAccountId}/handlingUnits/${params.value}`
              }
              state={{
                dealerAccountId,
              }}
              data-testid={`${params.value}-handlingUnit-details-link`}
              style={{
                color: theme.tokens.FuelColorTextBrandPrimary,
              }}
            >
              {params.value}
            </Link>
          )
        : undefined,
      renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
        <ControlledHeaderFilter
          dataTestId="handling-unit-search"
          inputRef={inputRef}
          disabled={disableFilters}
          value={filters.handlingUnitNumber ?? ""}
          onChange={(value) =>
            setFilterValue(value, FilterType.HandlingUnitNumber)
          }
          onClear={() => clearFilterValue(FilterType.HandlingUnitNumber)}
        />
      ),
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: "dealerReference",
      headerName: gridTexts.dealerReference,
      renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
        <ControlledHeaderFilter
          dataTestId="dealerReference-search"
          inputRef={inputRef}
          disabled={disableFilters}
          value={filters.dealerReference ?? ""}
          onChange={(value) =>
            setFilterValue(value, FilterType.DealerReference)
          }
          onClear={() => clearFilterValue(FilterType.DealerReference)}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "invoiceDate",
      headerName: gridTexts.createdDate,
      renderHeaderFilter: () => (
        <InvoicesDateRangePicker
          disabled={disableFilters}
          startDate={filters.startDate}
          endDate={filters.endDate}
          minDate={filters.minDate}
          onDateRangeChange={(startDate: Dayjs, endDate: Dayjs) =>
            onDateRangeChange(startDate, endDate)
          }
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "viewedDate",
      headerName: gridTexts.viewedDate,
      renderHeaderFilter: () => null,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "viewedBy",
      headerName: gridTexts.viewedBy,
      renderHeaderFilter: () => null,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={renderCellStyle}>{params.value}</Typography>
      ),
      disableColumnMenu: true,
      flex: 1,
      minWidth: 75,
    },
    {
      field: "actions",
      headerName: gridTexts.actions,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      type: "actions",
      flex: 1,
      minWidth: 75,
      renderCell: (params: GridRenderCellParams) => (
        <>
          {params.row.showActions && (
            <InvoicesGridActionMenu
              onActionOpen={() =>
                invoicesGridActionMenu.onActionOpen(
                  params.row.invoiceNumber,
                  params.row.dealerAccount,
                  params.row.mruSalesOrg
                )
              }
              onActionClose={() => invoicesGridActionMenu.onActionClose()}
              actions={{
                ...invoicesGridActionMenu.actions,
                returns: {
                  ...invoicesGridActionMenu.actions.returns,
                  onCreateReturnClick: () =>
                    invoicesGridActionMenu.actions.returns.onCreateReturnClick(
                      params.row.dealerAccount
                    ),
                },
              }}
              isLoading={invoicesGridActionMenu.isLoading}
            />
          )}
        </>
      ),
    },
  ];
}

function CustomGridCheckboxRenderer({
  params,
  onRowSelectChange,
}: {
  params: GridRenderCellParams;
  onRowSelectChange: (value: boolean, rowId: GridRowId) => void;
}) {
  const apiRef = useGridApiContext();
  useGridApiEventHandler(
    apiRef,
    "rowSelectionCheckboxChange",
    (selectionParams) => {
      onRowSelectChange(selectionParams.value, selectionParams.id);
    }
  );
  return <GridCellCheckboxRenderer {...params} />;
}

function CustomGridHeaderCheckbox({
  params,
  onHeaderSelectChange,
}: {
  params: GridColumnHeaderParams;
  onHeaderSelectChange: (value: boolean) => void;
}) {
  const apiRef = useGridApiContext();
  useGridApiEventHandler(
    apiRef,
    "headerSelectionCheckboxChange",
    (selectionParams) => {
      onHeaderSelectChange(selectionParams.value);
    }
  );
  return <GridHeaderCheckbox {...params} />;
}

export interface GridTexts {
  invoiceNumber: string;
  dealerAccount: string;
  nfeNumber: string;
  factura: string;
  officialDocumentNumber: string;
  documentNumber: string;
  handlingUnit: string;
  dealerReference: string;
  createdDate: string;
  invoiceType: string;
  orderType: string;
  viewedDate: string;
  viewedBy: string;
  actions: string;
}
